import { LegalShell, Section, P, UL } from "@/components/site/Legal";
import { CONTACT } from "@/lib/site";

export default function Privacy() {
  return (
    <LegalShell
      seoTitle="Privacy Notice | Eminence Windows & Doors"
      seoDescription="How Eminence Windows & Doors Ltd collects, uses and protects your personal information, and your rights under UK data protection law."
      kicker="Legal"
      title="Privacy Notice"
      updated="September 2026"
    >
      <Section>
        <P>
          This Privacy Notice explains how <strong>Eminence Windows &amp; Doors Ltd</strong> ("we", "us",
          "our") collects and uses your personal information, and the rights you have under UK data
          protection law. Eminence Windows &amp; Doors Ltd is responsible for (the "controller" of) the
          personal information covered by this notice.
        </P>
        <P>
          Registered address: {CONTACT.address}.<br />
          Email: <a href={CONTACT.emailHref} className="underline hover:text-gold-deep">{CONTACT.email}</a>.<br />
          Website: <a href="https://eminencewindows.co.uk" className="underline hover:text-gold-deep">https://eminencewindows.co.uk</a>.
        </P>
      </Section>

      <Section heading="Information we collect">
        <P>
          When you request a quotation, contact us, become a customer or otherwise communicate with us,
          we may collect information including:
        </P>
        <UL
          items={[
            "your name",
            "your email address",
            "your telephone number",
            "your postcode and/or property address",
            "details of the windows, doors or other products/services you are interested in",
            "information you provide in a quotation or contact form",
            "your correspondence and communications with us",
            "information reasonably necessary to prepare, administer and fulfil an order where you become a customer",
          ]}
        />
        <P>
          Our website quote/contact forms send your enquiry to {CONTACT.email}. These enquiries are stored
          in that email account. Emergent (the platform used to build and host the website) is not
          intentionally being used as Eminence's customer enquiry database.
        </P>
      </Section>

      <Section heading="How we use personal information">
        <P>We may use your information to:</P>
        <UL
          items={[
            "respond to your enquiries",
            "provide and follow up quotations",
            "arrange home quotation appointments",
            "communicate with prospective and existing customers",
            "process and administer orders",
            "arrange manufacture, delivery and installation where applicable",
            "provide customer service and aftercare",
            "deal with guarantees, complaints or remedial work",
            "maintain appropriate business and financial records",
            "comply with legal, tax, accounting and regulatory requirements",
            "protect our legitimate business interests where appropriate",
          ]}
        />
        <P>
          Our lawful bases for processing under UK GDPR depend on what we are doing with your information:
        </P>
        <UL
          items={[
            "Steps taken at your request before entering into a contract — for example, preparing and providing a quotation you have asked for.",
            "Performance of a contract — where you place an order, to process, administer and fulfil that order including manufacture, delivery, installation, aftercare and guarantees.",
            "Legitimate interests — for example, responding to enquiries, following up quotations, keeping appropriate business records and protecting our business, where this is not overridden by your rights.",
            "Legal obligation — for example, meeting our tax, accounting and other legal or regulatory duties.",
            "Consent — where consent is actually required, such as certain optional cookies and any optional marketing. You can withdraw consent at any time.",
          ]}
        />
        <P>
          We do not rely on consent as the lawful basis for everything — most of what we do relies on the
          other bases above.
        </P>
      </Section>

      <Section heading="How long we keep it (retention)">
        <P>
          Where a quotation or enquiry does not result in an order, we will ordinarily keep the related
          personal information for up to 12 months after the enquiry or quotation, unless there is a
          legitimate or legal reason to keep it for longer.
        </P>
        <P>
          Where you place an order and become a customer, relevant customer, contractual, financial,
          installation and guarantee records may need to be kept for longer periods in line with our
          legal, tax, accounting, contractual, guarantee and legitimate business requirements.
        </P>
      </Section>

      <Section heading="Sharing your information">
        <P>
          Where necessary, we may share your information with appropriate service providers and parties
          involved in delivering our service to you, such as manufacturers and suppliers, installers and
          subcontractors, professional advisers, IT, email and website providers, payment and accounting
          providers, guarantee or insurance-backed guarantee providers, and regulatory or certification
          bodies where applicable.
        </P>
        <P>We do not sell your personal information to third parties.</P>
      </Section>

      <Section heading="Website analytics">
        <P>
          Our website uses Google Analytics 4 (GA4) to help us understand website traffic and how visitors
          use the site. Our GA4 Measurement ID is <strong>G-3XD9L4B4C6</strong>.
        </P>
        <P>
          Our GA4 setup measures things such as page views, quote call-to-action clicks, successful lead
          submissions, telephone-link clicks and email-link clicks, along with useful non-identifying
          context such as product type and which button or link was used. We do not intentionally send
          your name, email address, telephone number, postcode, property address or quote-form contents
          to GA4 as ordinary analytics event data.
        </P>
        <P>
          GA4 operates using Google's <strong>Advanced Consent Mode</strong>. Before you make a choice,
          and if you decline analytics, analytics cookies and storage remain denied, although GA4 may
          still send limited, cookieless consent-mode signals that Google can use for aggregate and
          modelled measurement. If you accept analytics, GA4 measures your visit normally with cookies.
          See our <a href="/cookies" className="underline hover:text-gold-deep">Cookie Policy</a> to
          manage your choice.
        </P>
      </Section>

      <Section heading="Your rights">
        <P>Under UK data protection law you have rights, in certain circumstances, to:</P>
        <UL
          items={[
            "access the personal information we hold about you",
            "ask us to correct inaccurate or incomplete information",
            "ask us to erase your information",
            "ask us to restrict how we use your information",
            "object to our use of your information",
            "data portability, where applicable",
            "withdraw your consent, where our processing is based on consent",
          ]}
        />
        <P>
          To exercise any of these rights, please contact us at{" "}
          <a href={CONTACT.emailHref} className="underline hover:text-gold-deep">{CONTACT.email}</a>.
        </P>
        <P>
          If you are unhappy with how we have handled your information, you also have the right to complain
          to the Information Commissioner's Office (ICO), the UK's data protection regulator, at{" "}
          <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="underline hover:text-gold-deep">https://ico.org.uk</a>.
          We would, however, appreciate the chance to address your concerns first.
        </P>
      </Section>
    </LegalShell>
  );
}
